<script>
import LabelDropdownItem from './LabelDropdownItem.vue';
import Hotkey from 'dashboard/components/base/Hotkey.vue';
import AddLabelModal from 'dashboard/routes/dashboard/settings/labels/AddLabel.vue';
import { picoSearch } from '@scmmishra/pico-search';
import { sanitizeLabel } from 'shared/helpers/sanitizeData';

export default {
  components: {
    LabelDropdownItem,
    AddLabelModal,
    Hotkey,
  },

  props: {
    accountLabels: {
      type: Array,
      default: () => [],
    },
    selectedLabels: {
      type: Array,
      default: () => [],
    },
    allowCreation: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update', 'add', 'remove'],

  data() {
    return {
      search: '',
      createModalVisible: false,
    };
  },

  computed: {
    createLabelPlaceholder() {
      const label = this.$t('CONTACT_PANEL.LABELS.LABEL_SELECT.CREATE_LABEL');
      return this.search ? `${label}:` : label;
    },

    filteredActiveLabels() {
      if (!this.search) return this.accountLabels;

      return picoSearch(this.accountLabels, this.search, ['title'], {
        threshold: 0.9,
      });
    },

    noResult() {
      return this.filteredActiveLabels.length === 0;
    },

    hasExactMatchInResults() {
      return this.filteredActiveLabels.some(
        label => label.title === this.search
      );
    },

    shouldShowCreate() {
      return this.allowCreation && this.filteredActiveLabels.length < 3;
    },

    parsedSearch() {
      return sanitizeLabel(this.search);
    },
  },

  mounted() {
    this.focusInput();
  },

  methods: {
    focusInput() {
      this.$refs.searchbar.focus();
    },

    updateLabels(label) {
      this.$emit('update', label);
    },

    onAdd(label) {
      this.$emit('add', label);
    },

    onRemove(label) {
      this.$emit('remove', label);
    },

    onAddRemove(label) {
      if (this.selectedLabels.includes(label.title)) {
        this.onRemove(label.title);
      } else {
        this.onAdd(label);
      }
    },

    showCreateModal() {
      this.createModalVisible = true;
    },

    hideCreateModal() {
      this.createModalVisible = false;
    },
  },
};
</script>

<template>
  <div
    class="flex flex-col w-full max-h-[12.5rem] shadow-xl rounded-xl border border-slate-100 bg-white dark:bg-slate-800 dark:border-slate-700"
  >
    <!-- Sticky Create Button -->
    <div
      v-if="allowCreation && shouldShowCreate"
      class="sticky top-0 z-10 bg-white dark:bg-slate-800 p-2"
    >
      <button
        type="button"
        class="w-full px-3 py-2 rounded-md text-sm text-slate-900 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-600 flex items-center transition-colors"
        :disabled="hasExactMatchInResults"
        @click="showCreateModal"
      >
        <i class="i-lucide-plus mr-2 text-sm flex-shrink-0" />
        <span class="flex-grow text-left">Criar etiqueta</span>
        <span
          v-if="parsedSearch"
          class="text-slate-500 dark:text-slate-400 ml-1"
          >{{ parsedSearch }}</span>
      </button>

      <woot-modal v-model:show="createModalVisible" :on-close="hideCreateModal">
        <AddLabelModal :prefill-title="parsedSearch" @close="hideCreateModal" />
      </woot-modal>
    </div>

    <!-- Sticky Search Bar -->
    <div
      class="sticky top-0 px-2 border-b border-slate-100 dark:border-slate-700"
    >
      <input
        ref="searchbar"
        v-model="search"
        type="text"
        autofocus="true"
        :placeholder="$t('CONTACT_PANEL.LABELS.LABEL_SELECT.PLACEHOLDER')"
        class="busca-dropdown-margin"
      />
    </div>

    <!-- Scrollable Labels List -->
    <div class="flex-auto overflow-auto px-1 py-1 scrollbar-label-list">
      <div class="w-full">
        <LabelDropdownItem
          v-for="label in filteredActiveLabels"
          :key="label.title"
          :title="label.title"
          :color="label.color"
          :selected="selectedLabels.includes(label.title)"
          @select-label="onAddRemove(label)"
        />
        <div
          v-if="noResult"
          class="flex justify-center py-4 px-2.5 font-medium text-xs text-slate-700 dark:text-slate-200"
        >
          {{ $t('CONTACT_PANEL.LABELS.LABEL_SELECT.NO_RESULT') }}
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.hotkey {
  @apply flex-shrink-0;
}

.button-new-label {
  @apply whitespace-nowrap text-ellipsis overflow-hidden items-center;

  .icon {
    @apply min-w-0;
  }
}

.busca-dropdown-margin {
  margin-top: 0.5rem !important;
  margin-bottom: 0.5rem !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}

/* Custom scrollbar styles */
.scrollbar-label-list {
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;
}

.scrollbar-label-list::-webkit-scrollbar {
  width: 8px;
}

.scrollbar-label-list::-webkit-scrollbar-track {
  background: transparent;
  border-radius: 10px;
}

.scrollbar-label-list::-webkit-scrollbar-thumb {
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  transition: background-color 0.3s ease;
}

.scrollbar-label-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(0, 0, 0, 0.3);
}

.scrollbar-label-list::-webkit-scrollbar-thumb:active {
  background-color: rgba(0, 0, 0, 0.4);
}

/* Tema escuro */
.dark .scrollbar-label-list {
  scrollbar-color: rgba(255, 255, 255, 0.2) transparent;
}

.dark .scrollbar-label-list::-webkit-scrollbar-thumb {
  background-color: rgba(255, 255, 255, 0.2);
}

.dark .scrollbar-label-list::-webkit-scrollbar-thumb:hover {
  background-color: rgba(255, 255, 255, 0.3);
}

.dark .scrollbar-label-list::-webkit-scrollbar-thumb:active {
  background-color: rgba(255, 255, 255, 0.4);
}
</style>